import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import './index.scss'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { MANAGEMENT_DATA_IDS, MANAGEMENT_VIEWS } from './data'
import Table from '../../components/Table'
import Button from '../../components/Button'
import { useQuery } from 'react-query'
import { useModal } from '../../hooks/useModal'
import AddItemForm from '../../components/AddItemForm'
import { getMobileList } from '../../services/calls/mobiles'
import UserContext from '../../contexts/UserContext/context'
import { ICON } from '../../components/Icon/data'
import Icon from '../../components/Icon'
import { useToast } from '../../hooks/useToast'
import CarsInFleetColumn from './table/CarsInFleetColumn'
import FleetIconsColumn from './table/FleetIconsColumn'
import UserFleetColumn from './table/UserFleetColumn'
import { useFormat } from '../../hooks/useFormat'
import Image from '../../components/Image'
import CustomIconColumn from './table/CustomIconColumn'
import { getSensors } from '../../services/calls/sensors'
import { isValue } from '../../services/utils/misc'

export type DataDump = {
  cars?: Record<string | number, string>
  fleets?: Record<string | number, string>
  sensors?: Record<string | number, string>
}

const Management = () => {
  const modalRef = useRef()
  const tableRef = useRef()

  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { view } = useParams()
  const { openModal, closeModal, isOpen: isModalOpen } = useModal()
  const { addToast } = useToast()
  const { formatFunction } = useFormat()

  const [dynamicVal, setDynamicVal] = useState<Record<string, any>>({})

  const viewInfo = useMemo(() => MANAGEMENT_VIEWS[view] ?? {}, [view])

  const { data, refetch, isLoading } = useQuery([`getManagement__${view}`], () => viewInfo.getFunction())
  const { data: cars } = useQuery([`getCars__${view}`], () => getMobileList({}))
  const { data: sensors } = useQuery([`getSensors__${dynamicVal.mobile_id}`], () => getSensors({ mobile: dynamicVal.mobile_id }))

  useEffect(() => {
    if (Object.keys(viewInfo).length) tableRef?.current?.setSort({ column: viewInfo.default_sort, type: 'ascending' })
  }, [viewInfo])

  const dataDump = useMemo(() => {
    const lookup: DataDump = {}
    if (cars) {
      cars.forEach((car) => {
        if (!lookup.cars) lookup.cars = {}
        lookup.cars[car.id] = car.globalid
      })
    }
    
    if (user.available_fleets.length) {
      user.available_fleets.forEach((fleet) => {
        if (!lookup.fleets) lookup.fleets = {}
        lookup.fleets[fleet.fleetid] = fleet.name
      })
    }

    if (sensors) {
      sensors.forEach((sensor) => {
        if (!lookup.sensors) lookup.sensors = {}
        lookup.sensors[sensor.id] = sensor.description
      })
    }

    return lookup
  }, [cars, user.available_fleets, sensors])

  useEffect(() => {
    if (isModalOpen) modalRef?.current?.setOptions(dataDump)
  }, [dataDump, isModalOpen])

  const deleteAction = useCallback(async (id: number) => {
    try {
      await viewInfo.deleteFunction(id)
      refetch()
      closeModal()
      setDynamicVal({})
    } catch (err: any) {
      if (err.response?.data) {
        addToast({
          status: "error",
          type: "toast",
          message: err.response.data?.detail ?? t('An error came up with your delete request')
        })
      }
    }
  }, [viewInfo, addToast, refetch, closeModal])

  const startDelete = useCallback((data: any) => {
    closeModal() // Clear everything first
    openModal({
      header: `${t('Delete')} ${viewInfo.item}`,
      content: `${t('Are you sure you want to delete the {{item}}, "{{id}}"?', { item: viewInfo.item, id: data[viewInfo.mainRow] })}`,
      customButtons: (
        <div className="Management__modal-buttons__right">
          <Button
            label={t('Cancel')}
            onClick={() => {
              closeModal()
              setDynamicVal({})
            }}
            type="tertiary"
          />
          <Button
            label={t('Yes, delete it')}
            onClick={async () => deleteAction(data[viewInfo.idKey])}
          />
        </div>
      )
    })
  }, [openModal, viewInfo, closeModal, deleteAction])

  const saveAction = useCallback(async (data: any) => {
    try {
      const newData = modalRef?.current?.getData()
      const resp = await viewInfo.saveFunction(newData, newData?.[viewInfo.idKey], data)
      if (resp.status === 'success') {
        await refetch()
        closeModal()
        setDynamicVal({})
      } else if (resp.error) {
        modalRef.current.setError(resp.error)
      } else {
        addToast({
          status: "error",
          type: "toast",
          message: t('An error came up with your save request')
        })
      }
    } catch (err: any) {
      if (err.response?.data) {
        addToast({
          status: "error",
          type: "toast",
          message: err.response.data?.detail ?? t('An error came up with your save request')
        })
      }
    }
  }, [viewInfo, modalRef.current, refetch, closeModal, addToast])

  const modalHeader = useCallback((data?: any) => {
    const { hasModalPhoto, photoPath } = viewInfo
    const text = `${data ? t('Edit') : t('Add')} ${viewInfo?.item}`
    if (hasModalPhoto) {
      return (
        <div className="Management__modal-header">
          <Image url={data[photoPath]} />
          {text}
        </div>
      )
    }
    return text
  }, [t, viewInfo])

  const onValueChange = useCallback((key: string, value: any) => {
    if (key === 'mobile_id') setDynamicVal({ ...dynamicVal, [key]: value })
  }, [dynamicVal])

  const openForm = useCallback((data?: any) => {
    if (view === 'sensors' && data?.[viewInfo.idKey]) setDynamicVal({ mobile_id: data.mobile_id })
    const inputs = viewInfo.data.filter((input) => !input.onlyInTable)
    const watcher = viewInfo?.watchKeys?.reduce((obj, input) => {
      const key = input.path
      obj[key] = onValueChange
      return obj
    }, {})

    const newData = { ...data }
    if (view === 'mobiles' && data?.[viewInfo.idKey]) {
      newData.all_fleets = data.all_fleets.map((fleet) => fleet.fleet_id)
    }

    openModal({
      header: modalHeader(newData),
      content: (
        <AddItemForm
          ref={modalRef}
          inputs={inputs}
          default_data={newData}
          dataInfo={dataDump}
          onValueChange={watcher}
        />
      ),
      customButtons: (
        <div className="Management__modal-buttons">
          {data?.[viewInfo.idKey] && viewInfo.isDeletable(data) && (
            <Button
              size="l"
              type="tertiary"
              label={(
                <>
                  {`${t('Delete')} ${viewInfo.item}`}
                  <Icon icon={ICON.TRASH} />
                </>
              )}
              onClick={() => startDelete(data)}
            />
          )}
          <div className="Management__modal-buttons__right">
            <Button
              label={t('Cancel')}
              onClick={() => {
                closeModal()
                setDynamicVal({})
              }}
              type="tertiary"
            />
            <Button
              label={t('Save')}
              onClick={async () => saveAction(newData)}
            />
          </div>
        </div>
      )
    })
  }, [
    openModal, viewInfo, dataDump, t, closeModal, saveAction, deleteAction, modalRef,
    onValueChange, view, startDelete, modalHeader
  ])

  const mappedHeaderData = useMemo(() => {
    return viewInfo?.data
      ?.filter((col) => !col.onlyInForm)
      .reduce((obj, val, index) => {
        const { label, id, format } = val
        obj[id] = {
          ...val,
          label: typeof label === 'function' ? t(label(val)) : t(label),
          index,
          formatFunction: (val, row) => {
            if (id === viewInfo.mainRow) {
              return (
                <div
                  className="Management__clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => openForm(row)}
                >
                  {val}
                </div>
              )
            } else if (id === MANAGEMENT_DATA_IDS.FLEET_CARS) {
              return <CarsInFleetColumn row={row} />
            } else if (id === MANAGEMENT_DATA_IDS.FLEET_ICON) {
              return <FleetIconsColumn row={row} />
            } else if (id === MANAGEMENT_DATA_IDS.PERMISSION_FLEET) {
              return <UserFleetColumn row={row} />
            } else if (id === MANAGEMENT_DATA_IDS.FLEET_ACCESS) {
              return val.reduce((arr, fleet) => {
                arr.push(fleet.fleet_name)
                return arr
              }, []).join(', ')
            } else if (Array.isArray(val)) {
              return val.join(', ')
            } else if (id === MANAGEMENT_DATA_IDS.IMAGE) {
              return <Image url={val} />
            } else if (id === MANAGEMENT_DATA_IDS.CUSTOM_ICON) {
              return <CustomIconColumn val={val} />
            } else if (format) {
              return formatFunction(val, row, id, format)
            }
            
            return val
          }
        }
        return obj
    }, {})
  }, [viewInfo, openForm, formatFunction])

  const rows = useMemo(() => {
    if (!data) return []
    return data.reduce((arr, val) => {
      const row = viewInfo?.data?.reduce((obj, col) => {
        const { path, id, decimal, type } = col
        let value = val[path] ?? ''
        if (type === 'number' && isValue(decimal)) {
          value = val[path].toFixed(decimal)
          obj[path] = value
        }
        obj[id] = value
        return obj
      }, {})
      arr.push({...val, ...row})
      return arr
    }, [])
  }, [data, viewInfo])

  const addNewButton = useMemo(() => {
    if (viewInfo.canAddNew) {
      return (
        <Button
          label={t(viewInfo.addNewLabel)}
          onClick={() => {
            openForm()
          }}
        />
      )
    }
    return null
  }, [t, viewInfo, openForm])

  return (
    <div className="Management">
      <h1>{t(viewInfo.label)}</h1>
      <Table
        ref={tableRef}
        isLoading={isLoading}
        tableHeader={mappedHeaderData}
        rows={rows}
        headerElems={[addNewButton]}
        rowHeight={viewInfo.rowHeight}
        headerRowHeight={50}
        defaultSort={{
          column: MANAGEMENT_DATA_IDS.SENSOR_NUMBER,
          type: 'ascending'
        }}
      />
    </div>
  )
}

export default Management
